import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { STORAGE_KEYS } from '../../modules/auth/constants';
import { BarChart3, Download, Filter, X, Calendar, User, Building, Tag, TrendingUp } from 'lucide-react';

const API_BASE = '/api/v2/category-correction-analytics';

export default function AdminCategoryCorrectionAnalytics() {
  const [summary, setSummary] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    employeeId: '',
    departmentId: '',
    categoryId: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);

      const response = await axios.get(`${API_BASE}/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSummary(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Load summary error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadCorrections = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);

      const response = await axios.get(`${API_BASE}/corrections?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCorrections(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load corrections');
      }
    } catch (err) {
      console.error('Load corrections error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load corrections');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);

      const response = await axios.get(`${API_BASE}/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'category-corrections.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      employeeId: '',
      departmentId: '',
      categoryId: ''
    });
  };

  useEffect(() => {
    loadSummary();
    loadCorrections();
  }, []);

  useEffect(() => {
    loadSummary();
    loadCorrections();
  }, [filters]);

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 48, height: 48, borderRadius: 10, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={24} color="#FFFFFF" />
      </div>
      <div>
        <div style={{ fontSize: 13, color: '#64748B', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: '#1E293B' }}>{value}</div>
      </div>
    </div>
  );

  const ListCard = ({ title, items, icon: Icon }) => (
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Icon size={18} color="#64748B" />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', margin: 0 }}>{title}</h3>
      </div>
      {items && items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#F8FAFC', borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: '#475569' }}>
                {item.category || item.employee_name || item.department_name || '—'}
                {item.subcategory && ` > ${item.subcategory}`}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{item.count}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: 20 }}>No data available</div>
      )}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", padding: 24, background: '#F1F5F9', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <BarChart3 size={28} color="#0F172A" />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: 0 }}>Category Correction Analytics</h1>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0F172A', cursor: 'pointer' }}
            >
              <Filter size={16} />
              Filters
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', opacity: exporting ? 0.5 : 1 }}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', margin: 0 }}>Filter Options</h3>
              <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer' }}>
                <X size={14} />
                Clear All
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                  <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                  <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                  <User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Employee ID
                </label>
                <input
                  type="text"
                  placeholder="Enter employee ID"
                  value={filters.employeeId}
                  onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                  <Building size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Department ID
                </label>
                <input
                  type="text"
                  placeholder="Enter department ID"
                  value={filters.departmentId}
                  onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                  <Tag size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Category ID
                </label>
                <input
                  type="text"
                  placeholder="Enter category ID"
                  value={filters.categoryId}
                  onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B' }}
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Loading analytics...</div>
        ) : summary ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 24 }}>
              <StatCard title="Total Corrected Tickets" value={summary.total_corrected_tickets} icon={TrendingUp} color="#8B5CF6" />
              <StatCard title="Total Corrections" value={summary.total_corrections} icon={BarChart3} color="#3B82F6" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
              <ListCard title="Most Wrongly Selected Categories" items={summary.most_wrongly_selected_categories} icon={Tag} />
              <ListCard title="Most Frequently Corrected Categories" items={summary.most_frequently_corrected_categories} icon={Tag} />
              <ListCard title="Corrections by Employee" items={summary.corrections_by_employee} icon={User} />
              <ListCard title="Corrections by Department" items={summary.corrections_by_department} icon={Building} />
            </div>

            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', marginBottom: 16 }}>Recent Corrections</h3>
              {corrections.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Ticket ID</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Employee</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Department</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Original Category</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Corrected Category</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Reason</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Corrected By</th>
                        <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Corrected At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {corrections.map((correction) => (
                        <tr key={correction.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: 12, color: '#1E293B' }}>{correction.ticket_code || correction.case_id}</td>
                          <td style={{ padding: 12, color: '#1E293B' }}>{correction.employee_name || '—'}</td>
                          <td style={{ padding: 12, color: '#1E293B' }}>{correction.department_name || '—'}</td>
                          <td style={{ padding: 12, color: '#1E293B' }}>
                            {correction.original_category || '—'}
                            {correction.original_subcategory && ` > ${correction.original_subcategory}`}
                          </td>
                          <td style={{ padding: 12, color: '#1E293B' }}>
                            {correction.corrected_category || '—'}
                            {correction.corrected_subcategory && ` > ${correction.corrected_subcategory}`}
                          </td>
                          <td style={{ padding: 12, color: '#1E293B', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{correction.correction_reason || '—'}</td>
                          <td style={{ padding: 12, color: '#1E293B' }}>{correction.corrected_by_name || '—'}</td>
                          <td style={{ padding: 12, color: '#1E293B' }}>{correction.corrected_at ? new Date(correction.corrected_at).toLocaleString('en-GB') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: 40 }}>No corrections found</div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
